/**
 * Labstep
 *
 * @module screens/Resource/Index/Master
 * @desc Resource Index Master
 */

import { EntityActionCreateMenu } from 'labstep-web/components/Entity/Action/Create/Menu';
import type { EntitySearchContainerChildrenProps } from 'labstep-web/components/Entity/Search/Children/types';
import { EntityImportModalTrigger as EntityImportModal } from 'labstep-web/components/EntityImport/Modal/Trigger';
import { ResourceEmptyState } from 'labstep-web/components/Resource/EmptyState';
import { ParamsStateContextProvider } from 'labstep-web/contexts/params-state';
import type { ParamsStateContextType } from 'labstep-web/contexts/params-state/types';
import { ExportProgressProvider } from 'labstep-web/grid/Index/components/DataGridExportAsCSV/context';
import { GridNewIndex } from 'labstep-web/grid/NewIndex';
import {
  getSystemFilterTemplateGuid,
  systemFilterNoTemplate,
} from 'labstep-web/grid/NewIndex/services/grid-new-index.service';
import { GridFilterService } from 'labstep-web/grid/services/grid-filter.service';
import { EntityView } from 'labstep-web/models/entity-view.model';
import { Resource } from 'labstep-web/models/resource.model';
import { MasterIndex } from 'labstep-web/screens/Master/Index';
import { ScreensResourceIndexBreadcrumb as ResourceIndexBreadcrumb } from 'labstep-web/screens/Resource/Index/Breadcrumb';
import { ScreensResourceIndexLeftPane as ResourceIndexLeftPane } from 'labstep-web/screens/Resource/Index/LeftPane';
import { LocationService } from 'labstep-web/services/location.service';
import { PostFilterComparison } from 'labstep-web/services/postFilter/constants';
import { PostFilterTag } from 'labstep-web/services/postFilter/filters/tag';
import type React from 'react';
import { useCallback, useMemo } from 'react';
import { withRouter } from 'react-router-dom';
import { bulkActions, filters, sortOptions } from './constants';
import type { ScreensResourceIndexMasterProps } from './types';

export const ScreensResourceIndexMaster: React.FC<
  ScreensResourceIndexMasterProps
> = ({ entityView, group, links, location }) => {
  const tagId = LocationService.getQueryParameter(location, 'tag_id');

  const breadcrumb = useMemo(() => <ResourceIndexBreadcrumb />, []);
  const leftPane = useMemo(
    () => <ResourceIndexLeftPane links={links} />,
    [links],
  );
  const noResultsMessage = useCallback(
    () => (
      <ResourceEmptyState
        resourceTemplateId={
          entityView.templateId && entityView.templateId !== 'none'
            ? entityView.templateId
            : undefined
        }
      />
    ),
    [entityView.templateId],
  );
  const action = useCallback(
    () => (
      <EntityActionCreateMenu
        entityName={Resource.entityName}
        extraOptions={
          <EntityImportModal
            targetEntityName={Resource.entityName}
            templateId={
              entityView.templateId &&
              entityView.templateId !== 'none'
                ? entityView.templateId
                : undefined
            }
          />
        }
        templateId={
          entityView.templateId && entityView.templateId !== 'none'
            ? entityView.templateId
            : undefined
        }
      />
    ),
    [entityView.templateId],
  );

  const globalParams: Record<
    string,
    string | number | Record<string, unknown>[]
  > = {
    group_id: group.id,
    skip_total: 1,
  };

  const paramsStateContext: ParamsStateContextType = {
    isGrid: true,
    name: 'resource_index',
    globalParams,
    entityView,
    loadedEntityView: new EntityView({
      ...entityView,
    }),
  };
  if (
    entityView.context === 'resource_index_all' &&
    !entityView.templateId
  ) {
    paramsStateContext.initialSearchParams =
      entityView.parameters || {};
  }
  if (
    entityView.context === 'resource_index' &&
    !entityView.templateId
  ) {
    paramsStateContext.initialSearchParams =
      GridFilterService.injectSystemFilter(
        systemFilterNoTemplate,
        entityView.parameters || {},
      );
  } else if (entityView.templateId) {
    paramsStateContext.initialSearchParams =
      GridFilterService.injectSystemFilter(
        getSystemFilterTemplateGuid(entityView.templateId),
        entityView.parameters || {},
      );
  }
  if (tagId) {
    paramsStateContext.initialSearchParams =
      GridFilterService.injectUserFilter(
        PostFilterTag.getNode([tagId, PostFilterComparison.eq]),
        paramsStateContext.initialSearchParams,
      );
  }
  if (paramsStateContext.loadedEntityView) {
    paramsStateContext.loadedEntityView.parameters =
      paramsStateContext.initialSearchParams;
  }
  const params = useMemo(
    () => ({
      ...paramsStateContext.initialSearchParams,
    }),
    [group.id, entityView.parameters],
  );

  return (
    <ParamsStateContextProvider value={paramsStateContext}>
      <ExportProgressProvider>
        <MasterIndex
          entityName={Resource.entityName}
          breadcrumb={breadcrumb}
          leftPane={leftPane}
          filters={filters}
          params={params}
          noResultsMessage={noResultsMessage}
          action={action}
          bulkActions={bulkActions}
          sortOptions={sortOptions}
          useAllPages
          usePostFilter
          tableFormat={false}
        >
          {({
            entities,
            readNextPage,
            status,
          }: EntitySearchContainerChildrenProps) => (
            <GridNewIndex
              entityName={Resource.entityName}
              group={group}
              entities={entities}
              entityView={entityView}
              status={status}
              loadMore={readNextPage}
            />
          )}
        </MasterIndex>
      </ExportProgressProvider>
    </ParamsStateContextProvider>
  );
};

export default withRouter(ScreensResourceIndexMaster);
